import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Sun, Sunrise, MapPin, Timer, Target, Trophy, Sparkles, Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AnimatedSun from './AnimatedSun';
import WhyThisMattersCard from '@/components/education/WhyThisMattersCard';
import { useWellnessEducation } from '@/hooks/useWellnessEducation';
import ConfettiCelebration from './ConfettiCelebration';
import StreakFlame from './StreakFlame';

interface SunlightTrackerProps {
  initialData?: {
    morning_sunlight?: boolean;
    sunlight_minutes?: number;
    sunlight_location?: string;
    sunlight_streak?: number;
  };
  onUpdate: (data: any) => void;
}

export default function SunlightTracker({ initialData = {}, onUpdate }: SunlightTrackerProps) {
  const [gotSunlight, setGotSunlight] = useState(initialData.morning_sunlight || false);
  const [minutes, setMinutes] = useState(initialData.sunlight_minutes || 0);
  const [location, setLocation] = useState(initialData.sunlight_location || '');
  const [streak, setStreak] = useState(initialData.sunlight_streak || 0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [animateSun, setAnimateSun] = useState(false);
  const { toast } = useToast();
  const { data: education } = useWellnessEducation('sunlight_tracking', 'circadian-health');

  const isGoalMet = gotSunlight && minutes >= 10;
  const targetMinutes = 15;
  const percentage = Math.min((minutes / targetMinutes) * 100, 100);

  useEffect(() => {
    onUpdate({
      morning_sunlight: gotSunlight,
      sunlight_minutes: minutes,
      sunlight_location: location,
      sunlight_streak: streak
    });
  }, [gotSunlight, minutes, location, streak, onUpdate]);

  const toggleSunlight = (checked: boolean) => {
    setGotSunlight(checked);
    setAnimateSun(true);
    
    if (checked) {
      if (minutes === 0) setMinutes(10); // Default to 10 minutes
      
      // Trigger celebration if goal is met
      if (minutes >= 10) {
        setShowCelebration(true);
        const newStreak = streak + 1;
        setStreak(newStreak);
        toast({
          title: "☀️ Sunshine Achieved!",
          description: `Amazing! You've got your daily vitamin D. ${newStreak} day streak!`,
        });
        setTimeout(() => setShowCelebration(false), 3000);
      } else {
        toast({
          title: "🌅 Sunlight logged!",
          description: "Don't forget to add your minutes for the full benefit."
        });
      }
    } else {
      toast({
        title: "Sunlight removed",
        description: "No worries, there's still time to catch some rays!"
      });
    }
    
    setTimeout(() => setAnimateSun(false), 2000);
  };

  const updateMinutes = (newMinutes: number) => {
    setMinutes(newMinutes);
    
    // Check if goal was just reached
    if (gotSunlight && !isGoalMet && newMinutes >= 10) {
      setShowCelebration(true);
      const newStreak = streak + 1;
      setStreak(newStreak);
      toast({
        title: "🎯 Sunlight Goal Achieved!",
        description: `Perfect! You've reached the recommended 10+ minutes. ${newStreak} day streak!`,
      });
      setTimeout(() => setShowCelebration(false), 3000);
    }
  };

  const getStreakMessage = () => {
    if (streak === 0) return "Start your sunshine streak today!";
    if (streak === 1) return "🌟 1 day streak! Keep the momentum going!";
    if (streak < 7) return `🔥 ${streak} days! Building a healthy habit!`;
    if (streak < 30) return `☀️ ${streak} days! You're a sunshine champion!`;
    return `🏆 ${streak} days! Vitamin D master!`;
  };

  const getBenefitMessage = () => {
    if (minutes === 0) return "Get some morning sunlight to boost your mood and energy!";
    if (minutes < 5) return "Great start! Try to get at least 5-10 minutes for mood benefits.";
    if (minutes < 10) return "Good! Just a few more minutes for optimal vitamin D production.";
    if (minutes < 20) return "Perfect! You're getting great vitamin D and mood benefits.";
    return "Excellent! You're maximizing all the benefits of natural sunlight.";
  };

  return (
    <div className="space-y-6">
      <ConfettiCelebration isActive={showCelebration} particleCount={40} />
      
      <Card className={`p-6 transition-all duration-500 overflow-hidden relative ${
        showCelebration ? 'border-primary shadow-lg shadow-primary/20' : 'border-border'
      }`}>
        {/* Animated sun rays background */}
        {gotSunlight && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 pointer-events-none opacity-10"
          >
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute top-1/2 left-1/2 w-1 bg-gradient-to-b from-primary to-transparent"
                style={{
                  height: '150%',
                  transform: `translate(-50%, -50%) rotate(${i * 45}deg)`,
                  transformOrigin: 'center center'
                }}
              />
            ))}
          </motion.div>
        )}

        <div className="text-center mb-6 relative z-10">
          {/* Big Animated Sun Character */}
          <motion.div 
            className="mb-6"
            animate={gotSunlight ? { scale: [1, 1.02, 1] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <AnimatedSun 
              isSmiling={gotSunlight}
              size="large"
              sunlightMinutes={minutes}
            />
          </motion.div>
          
          <h3 className="text-2xl font-bold mb-2 text-foreground">Morning Sunlight</h3>
          
          <AnimatePresence>
            {isGoalMet && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2 text-primary mb-2"
              >
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatDelay: 1 }}
                >
                  <Star className="w-5 h-5" />
                </motion.div>
                <span className="font-semibold">Daily Vitamin D Goal Met!</span>
                <motion.div
                  animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatDelay: 1 }}
                >
                  <Star className="w-5 h-5" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <motion.div 
            className="text-lg font-medium text-muted-foreground"
            key={minutes}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
          >
            {gotSunlight ? (
              <span className="text-foreground">
                <span className="text-2xl font-bold text-primary">{minutes}</span> minutes logged
              </span>
            ) : (
              "Tap below to log your sunlight"
            )}
          </motion.div>
        </div>

        {/* Sunlight Toggle */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 p-4 rounded-lg border bg-gradient-calm">
            <Checkbox
              id="morning-sunlight"
              checked={gotSunlight}
              onCheckedChange={toggleSunlight}
              className="scale-125"
            />
            <div className="flex-1">
              <Label htmlFor="morning-sunlight" className="text-lg font-semibold cursor-pointer flex items-center gap-2">
                <Sunrise className="w-5 h-5" />
                I got morning sunlight today
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                {getBenefitMessage()}
              </p>
            </div>
          </div>
        </div>

        {gotSunlight && (
          <>
            {/* Minutes and Progress */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Minutes (recommended: 10-20)</Label>
                  <Input
                    type="number"
                    placeholder="10"
                    value={minutes || ''}
                    onChange={(e) => updateMinutes(parseInt(e.target.value) || 0)}
                    min={0}
                    max={120}
                    className="text-center text-lg font-bold"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Location</Label>
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger>
                      <SelectValue placeholder="Where?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="garden">Garden/Yard</SelectItem>
                      <SelectItem value="balcony">Balcony</SelectItem>
                      <SelectItem value="window">By Window</SelectItem>
                      <SelectItem value="walk">Walk Outside</SelectItem>
                      <SelectItem value="park">Park</SelectItem>
                      <SelectItem value="beach">Beach</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Progress Visualisation */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-1">
                    <Target className="w-4 h-4" />
                    Goal Progress
                  </span>
                  <span className="font-medium">{Math.round(percentage)}%</span>
                </div>
                
                {/* Progress bar with sun visualisation */}
                <div className="relative">
                  <div className="h-6 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-1000 ease-out ${
                        isGoalMet ? 'bg-gradient-to-r from-primary to-primary-glow animate-success-glow' : 'bg-gradient-to-r from-primary/60 to-primary'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  
                  {/* Moving sun indicator */}
                  <div 
                    className="absolute top-0 h-6 w-6 transition-all duration-1000 ease-out"
                    style={{ left: `calc(${Math.min(percentage, 100)}% - 12px)` }}
                  >
                    <Sun className="w-6 h-6 text-primary animate-pulse" />
                  </div>
                </div>
                
                <div className="text-center text-xs text-muted-foreground">
                  {minutes < 10 ? `${10 - minutes} more minutes to reach your goal!` : "Goal achieved! ☀️"}
                </div>
              </div>
            </div>

            {/* Location Display */}
            {location && (
              <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-medium capitalize">{location.replace('_', ' ')}</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Streak Counter with Flame */}
        <motion.div 
          className={`p-4 rounded-xl transition-all duration-500 relative overflow-hidden ${
            isGoalMet ? 'bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 border border-primary/20' : 'bg-muted/30'
          }`}
          whileHover={{ scale: 1.01 }}
        >
          {streak >= 7 && (
            <motion.div
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-yellow-500/10 to-orange-500/10"
            />
          )}
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <StreakFlame streak={streak} size="sm" showLabel={false} />
              <div>
                <span className="font-bold text-lg text-foreground">
                  {streak} Day{streak !== 1 ? 's' : ''} Streak
                </span>
                <p className="text-sm text-muted-foreground">
                  {getStreakMessage()}
                </p>
              </div>
            </div>
            
            {streak >= 7 && (
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-6 h-6 text-primary" />
              </motion.div>
            )}
          </div>
        </motion.div>
      </Card>

      {education && (
        <WhyThisMattersCard
          toolName={education.tool_name}
          shortDescription={education.short_description}
          whyItMatters={education.why_it_matters}
          researchSummary={education.research_summary}
          howToOptimise={education.how_to_optimize}
          researchLinks={education.research_links}
          tags={education.tags}
        />
      )}
    </div>
  );
}